import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAuth from "@/modules/common/hooks/auth.hook";
import usePassport from "@/modules/common/hooks/passport";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import { useLeadContext } from "../context/lead-page.context";
import { getPendingContactConflicts } from "../api/contact-conflicts.api";
import { isContactConflictUid } from "../utils/contact-conflicts";

export function useContactConflictPermission() {
  const { user } = useAuth();
  const { permissions } = usePassport();
  return (
    !!user?.["cognito:groups"]?.includes("crm-super-admin") ||
    !!userHasPermission(
      {
        name: PermissionsEnum.RESOLVE_USERNAME_CONFLICT,
        level: PermissionLevel.WRITE,
      },
      permissions,
    )
  );
}

export function usePendingContactConflicts() {
  const allowed = useContactConflictPermission();
  const { lead } = useLeadContext();
  const journeyUId = lead?.data?.lead?.journey_uid;
  const query = useQuery({
    queryKey: ["contact-conflicts", journeyUId],
    queryFn: () => getPendingContactConflicts(journeyUId!),
    enabled: allowed && isContactConflictUid(journeyUId),
    staleTime: 15_000,
    retry: false,
  });
  return { ...query, data: allowed ? query.data : undefined, allowed };
}

export function useContactConflictLink() {
  const [searchParams, setSearchParams] = useSearchParams();
  return {
    conflictUid: searchParams.get("contactConflict"),
    openConflict: (uid: string) => {
      if (!isContactConflictUid(uid)) return;
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("contactConflict", uid);
        return next;
      });
    },
    closeConflict: () =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("contactConflict");
          return next;
        },
        { replace: true },
      ),
  };
}
